import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  getLocale,
  getLocaleChoice,
  subscribeToLocale,
  type Locale,
  type LocaleChoice,
} from "./locale.js";
import { catalogue, type MessageKey } from "./messages/index.js";
import { translate, type Vars } from "./translate.js";
import { dateFormats, weekdayNames, collationTag, type DateFormats } from "./formats.js";

export type { Locale, LocaleChoice } from "./locale.js";
export type { MessageKey } from "./messages/index.js";
export {
  LOCALES,
  LOCALE_LABEL,
  getLocale,
  getLocaleChoice,
  setLocaleChoice,
  applyLocale,
  readStoredChoice,
  resolveLocale,
  subscribeToLocale,
} from "./locale.js";
export { dateFormat, dateFormats, weekdayNames, collationTag } from "./formats.js";

/**
 * The words, in whichever language is on right now.
 *
 * There are two ways in on purpose. `t()` is a plain function, usable from the
 * outbox, the sync layer and anywhere else that is not a component. `useT()` is
 * the same lookup, but subscribed — a component using it re-renders when the
 * language changes, which a bare `t()` in a render body would not.
 */
export function t(key: MessageKey, vars?: Vars): string {
  const locale = getLocale();
  const entry = catalogue[key];

  // Unreachable through the type, but a stale key from persisted state would
  // otherwise render as nothing at all. The key itself is at least a clue.
  if (!entry) return key;

  return translate(entry[locale], locale, vars);
}

/** The language currently rendered, subscribed. */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribeToLocale, getLocale, getLocale);
}

/**
 * What was *picked*, which is not the same as what is rendered.
 *
 * The picker has to show "match the system" as its own state — reading the
 * resolved language instead would light up «Русский» for somebody who never
 * chose it, and their next visit from an English browser would silently move
 * the dot.
 */
export function useLocaleChoice(): LocaleChoice {
  return useSyncExternalStore(subscribeToLocale, getLocaleChoice, getLocaleChoice);
}

export type Translate = (key: MessageKey, vars?: Vars) => string;

/**
 * `t`, bound to the current language.
 *
 * The identity changes with the language, so a memoised child that took `t` as
 * a prop re-renders too instead of keeping the old words.
 */
export function useT(): Translate {
  const locale = useLocale();

  return useCallback(
    (key: MessageKey, vars?: Vars) => {
      const entry = catalogue[key];
      if (!entry) return key;
      return translate(entry[locale], locale, vars);
    },
    [locale],
  );
}

/** Date and time formatters for the current language. */
export function useDateFormats(): DateFormats {
  const locale = useLocale();
  return useMemo(() => dateFormats(locale), [locale]);
}

/** Пн … вс / Mon … Sun, for the current language. */
export function useWeekdayNames(): string[] {
  const locale = useLocale();
  return useMemo(() => weekdayNames(locale), [locale]);
}

/** The tag to pass to `localeCompare` and `toLocaleLowerCase`. */
export function useCollationTag(): string {
  const locale = useLocale();
  return collationTag(locale);
}
